import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedCart,
  TransformedCartShippingRate,
} from "../../../cart/types/cartApiTypes";
import CouponForm from "../../../cart/components/cartPage/CouponForm";

import CheckoutItemsList from "./CheckoutItemsList";
import CheckoutTotals from "./CheckoutTotals";

interface Message {
  type: "success" | "error";
  text: string;
}

interface OrderSummaryProps {
  cart: TransformedCart;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ cart }) => {
  const { t } = useTranslation();
  const currencySymbol = window.themeVariables?.currencySymbol || "₺";
  const [message, setMessage] = useState<Message | null>(null);

  const selectedShippingRate: TransformedCartShippingRate | undefined =
    cart.shippingRates
      .flatMap((pkg) => pkg.shippingRates)
      .find((rate) => rate.selected);

  const isFreeShipping = selectedShippingRate?.methodId === "free_shipping";

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="checkout-order-summary">
      <h2>{t("checkout.orderSummary", "Your Order")}</h2>

      <CheckoutItemsList items={cart.items} currencySymbol={currencySymbol} />

      <div className="coupon-actions-wrapper">
        <CouponForm
          appliedCoupons={cart.coupons.map((c) => c.code)}
          onCouponResult={setMessage}
          contextualClassName="coupon-actions--checkout-summary" // Pass the modifier class
        />
        {message && (
          <p className={`coupon-message ${message.type}`}>{message.text}</p>
        )}
      </div>

      <CheckoutTotals
        totals={cart.totals}
        fees={cart.fees}
        coupons={cart.coupons}
        needsShipping={cart.needsShipping}
        hasCalculatedShipping={cart.hasCalculatedShipping}
        isFreeShipping={isFreeShipping}
        currencySymbol={currencySymbol}
      />
    </div>
  );
};

export default OrderSummary;
